
import { h, defineComponent, onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

import Header from "@/components/Header.vue";
import InfoBlock from "@/components/InfoBlock.vue";
import RadioList from "@/components/RadioList.vue";
import CheckboxList from "@/components/CheckboxList.vue";
import FreeAnswer from "@/components/FreeAnswer.vue";
import CSIBlock from "@/components/CSIBlock.vue";
import FinalBlock from "@/components/FinalBlock.vue";
import SliderBlock from "@/components/SliderBlock.vue";
import RateBlock from "@/components/RateBlock.vue";
import ErrorBlock from "@/components/ErrorBlock.vue";

import surveysApi from "@/api/surveys";
import Answer from "@/types/Answer";
import Answers from "@/types/Answers";
import Question from "@/types/Question";
import ISurveyInfo from "@/types/SurveyInfo";
// import currentAnswers from "@/types/currentAnswers";

// UI
import Button from "@/components/UI/Button.vue";
import { LoadingOutlined } from "@ant-design/icons-vue";
import { Spin, Skeleton } from "ant-design-vue";
import { message } from "ant-design-vue";

// SWIPER
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/swiper-bundle.min.css";
import SwiperCore, { Mousewheel, Keyboard } from "swiper";
SwiperCore.use([Mousewheel, Keyboard]);

// moment for events
import moment from "moment";

export default defineComponent({
  name: "Home",

  components: {
    Header,
    ASkeleton: Skeleton,
    ASpin: Spin,
    InfoBlock,
    RadioList,
    CheckboxList,
    FreeAnswer,
    CSIBlock,
    FinalBlock,
    ErrorBlock,
    SliderBlock,
    RateBlock,
    Button,
    Swiper,
    SwiperSlide,
  },

  setup() {
    const store = useStore();

    //! main variable
    const route = useRoute();
    let instanceId = route.params.instanceId as string | null;

    // error mess
    const ruText = ref<string>();
    const kkText = ref<string>();
    const errorCode = ref<string>();
    const respStatus = ref<number>(200);

    // general
    const surveyInfo = ref<ISurveyInfo>();
    const displayType = ref<string>("ONE");

    // q/a
    const allQuestions = ref<Question[]>([]);
    const visibleQuestions = ref<Question[]>([]);
    const isVisibleSkeleton = ref<boolean>(false);
    const questions = ref<Question[]>([]);
    const answers = ref<Answers[]>([]);
    const sendingRequestStatus = ref<boolean>(false);
    const isLoaded = ref<boolean>(true);
    const responseLoading = ref<boolean>(false);
    const showSuccessMessage = ref<boolean>(false);
    const slider = ref();

    const indicator = h(LoadingOutlined, {
      style: {
        fontSize: "3.25em",
        // color: surveyInfo.value?.brand?.toLowerCase() === "activ" ? "#EE255C" : "#652D86",
        color: "var(--brand-color, #1d8de1)",
      },
      spin: true,
    });

    const answerValidation = ref<boolean[]>([]);

    const validation = computed(() => {
      return {
        notEmptyFields: answerValidation.value.every(
          (i: boolean) => i === true
        ),
        length: questions.value.length >= answers.value.length,
      };
    });

    interface IValidKey {
      [name: string]: boolean;
    }
    const isValid = computed(() => {
      let v: IValidKey = validation.value;
      return Object.keys(v).every(function (key) {
        return v[key];
      });
    });

    const currentEvent = computed(() => {
      let event: {
        startDate: string;
        endDate: string;
        event: string;
      }[] = [];
      const today = moment();
      if (surveyInfo.value?.surveyEvents) {
        surveyInfo.value.surveyEvents.forEach((item) => {
          if (today.isAfter(item.startDate) && today.isBefore(item.endDate)) {
            event.push(item);
          }
        });
      }
      return event[0]?.event;
    });

    const allRequiredAnswered = computed(() => {
      const requiredQuestions = visibleQuestions.value.filter((q) => {
        return q.answerTemplate?.answerType !== "NO_ANSWER";
      });
      return answers.value.length >= requiredQuestions.length;
    });

    const updateGeneralParams = (brand: string, lang: string) => {
      if (surveyInfo.value?.brand && surveyInfo.value?.brand !== brand) {
        sessionStorage.setItem("brand", brand);
        surveyInfo.value.brand = brand;
      }
      if (surveyInfo.value?.lang && surveyInfo.value?.lang !== lang) {
        surveyInfo.value.lang = lang;
      }
    };

    onMounted(async () => {
      await surveysApi
        .getSurveyInfo(instanceId as string)
        .then(async (res) => {
          surveyInfo.value = res;
          sessionStorage.setItem("brand", res.brand);
          store.dispatch("ACTION_SET_SURVEY_INFO", surveyInfo.value);

          displayType.value = res?.displayType || "ONE";
          store.dispatch("ACTION_SET_DISPLAY_TYPE", displayType.value);

          await uploadSurvey();
          isLoaded.value = false;

          document.body.classList.add(
            res.displayType === "ONE_PAGE" ? "body-one-page" : ""
          );
        })
        .catch((error) => {
          if (error.response.status >= 400) {
            questions.value = [];
            respStatus.value = error.response.status;
            kkText.value = error.response.data.messageKk;
            ruText.value = error.response.data.messageRu;
            errorCode.value = error.response.data.errorCode;
          }
          isLoaded.value = false;
        });
    });

    const onSwiper = (swiper: { allowTouchMove: boolean }) => {
      slider.value = swiper;
      slider.value.allowTouchMove = displayType.value === "ALL";
    };

    const compare = (prev: { order: number }, next: { order: number }) => {
      return prev.order - next.order;
    };

    const uploadSurvey = async () => {
      responseLoading.value = true;
      if (displayType.value === "ONE_PAGE") {
        await getInstanceOnePage();
      } else if (displayType.value === "ALL") {
        await surveysApi
          .getAllInstance(instanceId as string)
          .then((res) => {
            questions.value = res.filter(
              (i: { answerTemplate: any }) => i.answerTemplate !== null
            );
            // general
            if (res.length) {
              let [data] = res;
              updateGeneralParams(data.brand, data.lang);

              for (let i = 0; i < questions.value.length; i++) {
                if (
                  questions.value[i]?.answerTemplate?.answerType ===
                    "NO_ANSWER" ||
                  (questions.value[i]?.answerTemplate?.answerType ===
                    "CUSTOM" &&
                    !questions.value[i]?.answerTemplate?.customAnswerRequired)
                )
                  answerValidation.value[i] = true;
                else answerValidation.value[i] = false;
              }
            }
          })
          .catch((error) => {
            if (error.response.status >= 400) {
              questions.value = [];
              respStatus.value = error.response.status;
              kkText.value = error.response.data.messageKk;
              ruText.value = error.response.data.messageRu;
              errorCode.value = error.response.data.errorCode;

              if (showSuccessMessage.value) {
                let msg =
                  surveyInfo.value?.lang === "RU"
                    ? "Опрос успешно отправлен!"
                    : "Сауалнама сәтті жолданды!";
                if (kkText.value && ruText.value)
                  msg =
                    surveyInfo.value?.lang === "RU"
                      ? ruText.value
                      : kkText.value;

                message.success(msg);
              }
            }
          })
          .finally(() => {
            responseLoading.value = false;
          });
      } else if (displayType.value === "ONE") {
        await surveysApi
          .getInstance(instanceId as string)
          .then((res) => {
            updateGeneralParams(res.data.brand, res.data.lang);

            // Single question
            questions.value = [res.data];

            if (
              questions.value[0]?.answerTemplate?.answerType === "NO_ANSWER" ||
              (questions.value[0]?.answerTemplate?.answerType === "CUSTOM" &&
                !questions.value[0]?.answerTemplate?.customAnswerRequired)
            )
              answerValidation.value[0] = true;
            else answerValidation.value[0] = false;
          })
          .catch((error) => {
            if (error.response.status >= 400) {
              questions.value = [];
              respStatus.value = error.response.status;
              kkText.value = error.response.data.messageKk;
              ruText.value = error.response.data.messageRu;
              errorCode.value = error.response.data.errorCode;
            }
          })
          .finally(() => {
            responseLoading.value = false;
          });
      }
    };

    const getInstanceOnePage = async () => {
      responseLoading.value = true;

      await surveysApi
        .getAllInstance(instanceId as string)
        .then((res) => {
          allQuestions.value = res.map((q: Question) => ({
            ...q,
          }));

          updateGeneralParams(res[0]?.brand, res[0]?.lang);

          answerValidation.value = allQuestions.value.map((question) => {
            return (
              question.answerTemplate?.answerType === "NO_ANSWER" ||
              (question.answerTemplate?.answerType === "CUSTOM" &&
                !question.answerTemplate?.customAnswerRequired)
            );
          });

          // Добавляем только первый вопрос в массив отображаемых
          if (allQuestions.value.length > 0) {
            visibleQuestions.value = [allQuestions.value[0]];
          }
        })
        .catch((error) => {
          if (error.response.status >= 400) {
            respStatus.value = error.response.status;
            kkText.value = error.response.data.messageKk;
            ruText.value = error.response.data.messageRu;
            errorCode.value = error.response.data.errorCode;
          }
        })
        .finally(() => {
          responseLoading.value = false;
        });
    };

    const lenghtCriterias = (answerTemplate: any, csiType: string) => {
      let lenghtCriterias = 0;
      answerTemplate?.[csiType || "csiParams"].forEach((item: any) => {
        item.criterias.forEach(() => {
          lenghtCriterias++;
        });
      });
      return lenghtCriterias;
    };

    const nextAnswer = async () => {
      let value: any = { answerValue: "", csiAnswers: null };
      if (answers.value?.length) {
        value = answers?.value[0]?.answer;
      }

      if (
        (questions.value.length || allQuestions.value.length) &&
        answerValidation.value[0]
      ) {
        let currentIndex;
        let question;

        if (displayType.value === "ONE_PAGE") {
          currentIndex = 0;
          question = allQuestions.value[currentIndex];
        } else {
          [question] = questions.value;
        }
        let objectAnswer: Answer = value;

        responseLoading.value = true;
        if (objectAnswer.answerValue || objectAnswer.csiAnswers) {
          await surveysApi
            .addAnswer(
              instanceId as string,
              question.questionId as string,
              objectAnswer
            )
            .then(async () => {
              if (displayType.value === "ONE_PAGE") {
                await surveysApi
                  .updateAllInstance(instanceId as string, answers.value)
                  .then((res) => {
                    allQuestions.value = res.map((q: Question) => ({
                      ...q,
                    }));

                    answerValidation.value = allQuestions.value.map(
                      (question) => {
                        return (
                          question.answerTemplate?.answerType === "NO_ANSWER" ||
                          (question.answerTemplate?.answerType === "CUSTOM" &&
                            !question.answerTemplate?.customAnswerRequired)
                        );
                      }
                    );
                  });
              } else {
                await uploadSurvey();
              }
            })
            .catch((error) => {
              if (error.response.status >= 400) {
                if (displayType.value === "ONE_PAGE") {
                  allQuestions.value = [];
                } else {
                  questions.value = [];
                }
                if (surveyInfo.value?.surveyEvents)
                  surveyInfo.value.surveyEvents = [];
                respStatus.value = error.response.status;
                kkText.value = error.response.data.messageKk;
                ruText.value = error.response.data.messageRu;
                errorCode.value = error.response.data.errorCode;
              }
            })
            .finally(() => {
              responseLoading.value = false;
              objectAnswer = { answerValue: "" };
              if (displayType.value === "ONE_PAGE") {
                answers.value = [answers.value[0]];
                answerValidation.value = [answerValidation.value[0]];
              } else {
                answers.value = [];
              }
            });
        }
      }
    };

    const updateAnswerFields = async (i: number, value: any) => {
      let question =
        displayType.value === "ONE_PAGE"
          ? visibleQuestions.value[i]
          : questions.value[i];
      if (!question) return;

      let obj = {
        answer: {
          answerValue: "",
          csiAnswers: null,
        },
        questionId: "",
        index: 0,
      };

      const TYPE = question.answerTemplate.answerType;

      // CSI (Customer Satisfaction Index)
      if (TYPE === "CSI") {
        let csiChoiceVal = value.value;
        csiChoiceVal.forEach((item: any) => {
          delete item.csiParamId;
        });

        obj = {
          answer: {
            answerValue: "",
            csiAnswers: csiChoiceVal,
          },
          questionId: question.questionId,
          index: i,
        };

        answerValidation.value[i] = Boolean(obj.answer.csiAnswers) || false;
      } else {
        let multiChoiceVal = value?.myltiAnswer || "",
          customChoiseVal = value?.customAnswer || "",
          rateChoiceVal = value?.rateChoiceVal || 0,
          sliderChoiseVal = value?.sliderChoiseVal || 0,
          singleChoiseVal = value?.singleAnswer || "";

        if (TYPE === "MULTIPLE_CHOICE") {
          if (multiChoiceVal) {
            if (multiChoiceVal.length > 1) {
              multiChoiceVal = value.myltiAnswer.join("|");
            } else {
              multiChoiceVal = value.myltiAnswer.toString();
            }
          }
          if (customChoiseVal !== "" && multiChoiceVal.length > 0)
            customChoiseVal = "|" + customChoiseVal;
        }

        obj = {
          answer: {
            answerValue:
              question.startBlock === true
                ? ""
                : TYPE === "SINGLE_CHOICE"
                ? singleChoiseVal === "customAnswer"
                  ? customChoiseVal
                  : singleChoiseVal
                : TYPE === "MULTIPLE_CHOICE"
                ? customChoiseVal === ""
                  ? multiChoiceVal
                  : multiChoiceVal + customChoiseVal
                : TYPE === "CUSTOM"
                ? customChoiseVal
                : TYPE === "CSI"
                ? ""
                : TYPE === "RATING"
                ? rateChoiceVal
                : TYPE === "SLIDER"
                ? sliderChoiseVal
                : "",
            csiAnswers: null,
          },
          questionId: question.questionId,
          index: i,
        };

        answerValidation.value[i] = Boolean(obj.answer.answerValue) || false;
        const exception: boolean =
          (TYPE === "CUSTOM" &&
            !question.answerTemplate.customAnswerRequired) ||
          (TYPE === "RATING" && rateChoiceVal === 0) ||
          (TYPE === "SLIDER" && sliderChoiseVal === 0);
        if (exception) {
          answerValidation.value[i] = true;
        }
      }

      clearTimeout(window.timeout);
      window.timeout = setTimeout(async function () {
        if (
          (TYPE === "CSI" && obj?.answer?.csiAnswers) ||
          obj?.answer?.answerValue !== null ||
          obj?.answer?.answerValue !== undefined
        ) {
          answers.value.push(obj);
          // удаляем дубликаты
          answers.value = answers.value
            .reverse()
            .filter(
              (value, index, self) =>
                index ===
                self.findIndex((t) => t.questionId === value.questionId)
            );

          answers.value = answers.value.filter((ans) => ans.index <= i);
          visibleQuestions.value.splice(i + 1);

          if (displayType.value === "ONE_PAGE") {
            if (
              !(visibleQuestions.value.length - 1) &&
              i + 1 < allQuestions.value.length
            ) {
              isVisibleSkeleton.value = true;
            }

            await surveysApi
              .updateAllInstance(instanceId as string, answers.value)
              .then((res) => {
                allQuestions.value = res.map((q: Question) => ({
                  ...q,
                }));

                answerValidation.value = allQuestions.value.map((question) => {
                  return (
                    question.answerTemplate?.answerType === "NO_ANSWER" ||
                    (question.answerTemplate?.answerType === "CUSTOM" &&
                      !question.answerTemplate?.customAnswerRequired)
                  );
                });

                if (
                  visibleQuestions.value[visibleQuestions.value.length - 1] !==
                  visibleQuestions.value[i]
                ) {
                  visibleQuestions.value.splice(i + 1);
                }

                const nextQuestion = allQuestions.value[i + 1];
                if (
                  nextQuestion &&
                  answers.value[i] &&
                  !visibleQuestions.value.some(
                    (q) => q.questionId === nextQuestion?.questionId
                  )
                ) {
                  visibleQuestions.value.push(nextQuestion);
                }
              })
              .finally(() => {
                responseLoading.value = false;
                isVisibleSkeleton.value = false;
              });
          }

          // обновление логики для displayType ALL
          if (displayType.value === "ALL") {
            responseLoading.value = true;
            slider.value.disable();

            await surveysApi
              .updateAllInstance(instanceId as string, answers.value)
              .then((res) => {
                const earlyLength = questions.value.length;
                if (res.length) {
                  if (JSON.stringify(questions.value) !== JSON.stringify(res)) {
                    questions.value = res.filter(
                      (i: { answerTemplate: any }) => i.answerTemplate !== null
                    );
                  }

                  // general
                  let [data] = res;
                  updateGeneralParams(data.brand, data.lang);

                  // удаляем лишние ответы
                  const filteredAnswers = answers.value.filter(
                    (a: { questionId: string }) =>
                      questions.value.some(
                        (b: { questionId: string }) =>
                          b.questionId === a.questionId
                      )
                  );
                  answers.value = filteredAnswers;

                  // validation
                  if (questions.value.length !== earlyLength) {
                    let arr = [];
                    for (let ii = 0; ii < questions.value.length; ii++) {
                      let questionId: string = questions.value[ii].questionId,
                        ansCheck = answers.value?.find(
                          (a: { questionId: string }) =>
                            a.questionId === questionId
                        );

                      if (
                        ansCheck ||
                        questions.value[ii]?.answerTemplate?.answerType ===
                          "NO_ANSWER" ||
                        (questions.value[ii]?.answerTemplate?.answerType ===
                          "CUSTOM" &&
                          !questions.value[ii]?.answerTemplate
                            ?.customAnswerRequired)
                      )
                        arr[ii] = true;
                      else arr[ii] = false;
                    }

                    answerValidation.value = arr;
                  }

                  slider.value.update();
                }
              })
              .finally(() => {
                responseLoading.value = false;
                slider.value.enable();
              });
          }
        }
      }, 300);
    };

    const sendAnswers = async () => {
      if (answers.value.length) {
        sendingRequestStatus.value = true;
        slider.value.disable();

        // сортим по index
        answers.value.sort(
          (prev: { index: number }, next: { index: number }) =>
            prev.index - next.index
        );

        let lastIndex = questions.value.length - 1,
          ms = 0;

        if (
          questions.value[lastIndex].answerTemplate.answerType ===
            "NO_ANSWER" &&
          questions.value[lastIndex].endBlock
        ) {
          ms = 1000;
          slider.value.slideTo(lastIndex, ms / 2 || 0);
        }

        await surveysApi
          .addAnswers(instanceId as string, answers.value)
          .then(() => {
            showSuccessMessage.value = true;

            setTimeout(() => {
              answers.value = [];
              uploadSurvey();

              if (surveyInfo.value?.surveyEvents)
                surveyInfo.value.surveyEvents = [];
              slider.value.destroy();
            }, ms);
          })
          .catch((error) => {
            if (slider.value) slider.value.enable();
            if (
              error?.response?.data?.status &&
              error?.response?.data?.statusText
            )
              message.error(
                `${error.response.data.status} ${error.response.data.statusText}`
              );
          })
          .finally(() => {
            sendingRequestStatus.value = false;
          });
      }
    };

    const sendAnswerOnePage = async () => {
      if (!answers.value.length) return;

      sendingRequestStatus.value = true;

      try {
        await surveysApi.addAnswers(instanceId as string, answers.value);

        message.success(
          surveyInfo.value?.lang === "RU"
            ? "Опрос успешно отправлен!"
            : "Сауалнама сәтті жолданды!"
        );

        answers.value = [];
        visibleQuestions.value = [];
        await uploadSurvey();
      } catch (error: any) {
        if (error?.response?.data?.status) {
          message.error(
            `${error.response.data.status} ${error.response.data.statusText}`
          );
        }
      } finally {
        sendingRequestStatus.value = false;
      }
    };

    const nextSlide = () => {
      let activeIndex = slider.value.activeIndex,
        nextIndex = activeIndex + 1;

      if (typeof nextIndex === "number") {
        if (
          answerValidation.value[activeIndex] ||
          answerValidation.value[nextIndex]
        ) {
          nextIndex = answerValidation.value.findIndex(
            (item: boolean, index: number) => index !== activeIndex && !item
          );
          if (nextIndex < 0) {
            return;
          }
        } else if (nextIndex >= answerValidation.value.length) {
          nextIndex = answerValidation.value.findIndex(
            (item: boolean) => !item
          );
        }
        slider.value.slideTo(nextIndex, 500);
      }
    };

    return {
      //! main variable  //!
      route,
      instanceId,
      ruText,
      kkText,
      errorCode,
      respStatus,
      visibleQuestions,
      isVisibleSkeleton,
      allRequiredAnswered,

      //! main Function //!
      nextAnswer,
      uploadSurvey,
      questions,
      allQuestions,
      answers,
      answerValidation,
      compare,
      surveyInfo,
      displayType,
      updateAnswerFields,
      lenghtCriterias,
      sendAnswers,
      isValid,
      slider,
      onSwiper,
      nextSlide,
      isLoaded,
      responseLoading,
      sendingRequestStatus,
      indicator,
      currentEvent,
      sendAnswerOnePage,
    };
  },
});
